import { useIntl } from 'react-intl'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { DeliveryIcon, TakeoutIcon } from '@/components/common/icons'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '@/store'
import {
	selectSelectedOrderType,
	selectSelectedOrderTypeGuid,
	selectIsDeferred,
	selectExpectedTimeUtc,
	selectDineInTableName,
	selectNonDineInOrderTypeCount,
	selectDistanceLeadTimeInMinutes,
	selectHasFutureSlotsForTakeout,
	selectHasFutureSlotsForDelivery,
} from '@/store/ticketSlice'
import { useLocationRef } from '@/contexts/RefContext'
import { PATHS } from '@/router/paths'
import { convertMinutesToHoursAndMinutes, getAsapLeadTimeMinutes } from '@/lib/timeUtils'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { formatScheduledTime } from '@/lib/formatScheduledTime'
import {
	ActionsFooterRoot,
	OrderDetailsRow,
	OrderDetailsPairs,
	OrderDetailsPair,
	TimingPair,
	OrderTypeLabel,
	TimingText,
	TimingBold,
	EditIconButton,
} from './CartActionsFooter.styled'
import { CheckoutButton } from './CheckoutButton'

interface CartActionsFooterProps {
	timeZoneOffset?: number
	currency?: string
}

export function CartActionsFooter({ timeZoneOffset, currency }: CartActionsFooterProps) {
	const intl = useIntl()
	const { t } = useFormatMessage()
	const navigate = useNavigate()
	const locationRef = useLocationRef()

	const orderType = useAppSelector(selectSelectedOrderType)
	const selectedOrderTypeGuid = useAppSelector(selectSelectedOrderTypeGuid)
	const isDeferred = useAppSelector(selectIsDeferred)
	const expectedTimeUtc = useAppSelector(selectExpectedTimeUtc)
	const dineInTableName = useAppSelector(selectDineInTableName)
	const nonDineInOrderTypeCount = useAppSelector(selectNonDineInOrderTypeCount)
	const distanceLeadTimeInMinutes = useAppSelector(selectDistanceLeadTimeInMinutes)
	const hasFutureSlotsForTakeout = useAppSelector(selectHasFutureSlotsForTakeout)
	const hasFutureSlotsForDelivery = useAppSelector(selectHasFutureSlotsForDelivery)
	const isDineIn = selectedOrderTypeGuid === 'DINE_IN'
	// === false, not falsiness: null means the time-slots fetch failed or has not completed and
	// future slots may still exist, so only a confirmed empty result hides the edit affordance.
	const isTakeoutAsapOnly =
		orderType?.ref === 'TAKEOUT' &&
		hasFutureSlotsForTakeout === false &&
		nonDineInOrderTypeCount === 1
	// STO-3802: mirrors isTakeoutAsapOnly. Delivery ASAP with no future slots and no other
	// non-dine-in order type leaves nothing for the customer to change, so hide the edit affordance.
	const isDeliveryAsapOnly =
		orderType?.ref === 'DELIVERY' &&
		hasFutureSlotsForDelivery === false &&
		nonDineInOrderTypeCount === 1

	// Marketplace requires at least one order type (TAKEOUT or DELIVERY) to always be active,
	// so orderType is never null here in practice. This guard is defensive only.
	// CartBottomBar has no equivalent guard because it renders a fallback label for the mobile
	// sticky bar — the sidebar has no need to stay visible when there is nothing to show.
	if (!orderType) return null

	// PO demo parity: DineIn shows table name in place of ASAP timing; no edit.
	// Non-DineIn: standard ASAP / scheduled timing with edit pencil.
	const timingLabel = isDineIn
		? { bold: dineInTableName ?? '', regular: '' }
		: (() => {
				if (isDeferred && expectedTimeUtc) {
					return {
						bold: '',
						regular: formatScheduledTime(
							expectedTimeUtc,
							intl.locale,
							t('orderSettings:todayLabel'),
							timeZoneOffset,
							t('orderSettings:tomorrowLabel'),
							t('orderSettings:scheduledTimeAt'),
						),
					}
				}

				const leadMins = getAsapLeadTimeMinutes(orderType, distanceLeadTimeInMinutes)
				const duration =
					leadMins != null
						? `${intl.formatMessage({ id: 'orderSettings:asapSubtitleAbout' })} ${convertMinutesToHoursAndMinutes(leadMins)}`
						: intl.formatMessage({ id: 'orderSettings:asapSubtitleFallback' })
				return { bold: t('orderSettings:asapLabel'), regular: ` ${duration}` }
			})()

	function handleEditOrderSettings() {
		void navigate(`${PATHS.orderSettings(locationRef)}?edit=1`)
	}

	return (
		<ActionsFooterRoot>
			<OrderDetailsRow>
				<OrderDetailsPairs>
					<OrderDetailsPair>
						{/* DELIVERY → truck icon; all other types (TAKEOUT, DINE_IN, etc.)
						    fall through to the bag icon. Figma specifies only two icon variants
						    for this context — there is no dedicated DINE_IN icon here. */}
						{orderType.ref === 'DELIVERY' ? (
							<DeliveryIcon size={20} />
						) : (
							<TakeoutIcon size={20} />
						)}
						<OrderTypeLabel>{orderType.name}</OrderTypeLabel>
					</OrderDetailsPair>

					{(timingLabel.bold || timingLabel.regular) && (
						<TimingPair>
							<AccessTimeIcon
								sx={{ fontSize: 20, flexShrink: 0 }}
								aria-hidden="true"
							/>
							<TimingText
								title={
									`${timingLabel.bold}${timingLabel.regular}`.trim() || undefined
								}
							>
								{timingLabel.bold ? (
									<TimingBold>{timingLabel.bold}</TimingBold>
								) : null}
								{timingLabel.regular}
							</TimingText>
						</TimingPair>
					)}
				</OrderDetailsPairs>

				{!isDineIn && !isTakeoutAsapOnly && !isDeliveryAsapOnly && (
					<EditIconButton
						type="button"
						onClick={handleEditOrderSettings}
						aria-label={t('component:cartActionsFooter:editOrderSettings:label')}
					>
						<EditOutlinedIcon sx={{ fontSize: 20 }} aria-hidden="true" />
					</EditIconButton>
				)}
			</OrderDetailsRow>

			<CheckoutButton currency={currency} />
		</ActionsFooterRoot>
	)
}
